'use-strict';

/* @ngInject */
export default function WorkflowRuleFormController(
  $scope,
  toastService,
  $location,
  $routeParams,
  monitoringApiService,
  interfaceService,
  configService,
  tierService,
  numberConversionService,
) {
  const EVENT_RULE_TABLE = '/workflows/table';

  $scope.newRule = null;
  $scope.twoStepDelete = false;
  $scope.sources = require('./rule-form.sources.json');

  $scope.templates = require('./templates.json');
  $scope.templateCategories = [...new Set($scope.templates.map((t) => t.category))];
  $scope.templateToApply = null;

  $scope.selectedSource = null;
  $scope.showActions = false;
  $scope.preloader = false;
  $scope.templateDialog = false;
  $scope.preview = null;
  $scope.previewIsOutdated = true;
  $scope.previewIsGenerating = true;

  $scope.canCustomizeRule = tierService.authorizedToUseFeature('W_ADD') || false;

  /**
   * The user selects a data source.
   * Search the sources json for the provided sourceName
   */
  $scope.selectSource = () => {
    $scope.selectSource = $scope.sources.find((s) => s.value === $scope.rule.source);
  };

  $scope.$watch('rule.criteria', function(newVal, oldVal) {
    if (newVal !== oldVal) {
      $scope.previewIsOutdated = true;
    }
  }, true);

  /**
   * Convert the supplied `EventRule.criteria` to the correct form the server needs.
   * @param {Array<object>} criteria
   */
  const convertCriteria = (criteria) => {
    for (const c of criteria) {
      c.type = c.view.criterion_type;

      switch (c.operator) {
        case 'multi':
          c.type = 'multi';
          c.value = String(c.value).split(',');
          break;

        default:
          break;
      }

      switch (c.type) {
        case 'boolean':
          c.value = c.value === undefined ? false : c.value;
          break;

        case 'custom':
          c.value = c.view.value;
          break;

        default:
          break;
      }

      c.column = c.view.criterion_column;
      delete c.view;
    }
  };

  /**
   * Convert the supplied rule to the format the API server expects.
   * @param {object} rule
   */
  const prepareRuleForBackend = (rule) => {
    convertCriteria(rule.criteria);

    if (rule.execute_after) {
      rule.execute_after = numberConversionService.frontendStringToDate(rule.execute_after);
    }

    rule.notifications.event.assignedUsers = rule.notifications.event.assignedUsers ? rule.notifications.event.assignedUsers.split(',').map((o) => parseInt(o, 10)) : [];
    rule.notifications.task.assignedUsers = rule.notifications.task.assignedUsers ? rule.notifications.task.assignedUsers.split(',').map((o) => parseInt(o, 10)) : [];
    rule.notifications.task.assignedRoles = rule.notifications.task.assignedRoles ? rule.notifications.task.assignedRoles.split(',').map((o) => parseInt(o, 10)) : [];
  };

  $scope.setPreviewOutdated = () => {
    $scope.previewIsOutdated = true;
  };

  $scope.getPreview = async () => {
    const ruleCopy = JSON.parse(JSON.stringify($scope.rule));
    prepareRuleForBackend(ruleCopy);

    $scope.previewIsGenerating = true;

    const preview = await monitoringApiService.apiGetPreview(ruleCopy);
    $scope.preview = preview;
    $scope.previewIsOutdated = false;
    $scope.previewIsGenerating = false;
  };

  /**
   * Add the new `EventRule` to the API server.
   */
  const addNewRule = () => {
    return console.log(JSON.stringify($scope.rule));
    const postData = JSON.parse(JSON.stringify($scope.rule));
    prepareRuleForBackend(postData);
    monitoringApiService.apiAddEventRule(postData)
      .then(() => {
        toastService.toast('success', 'Das Monitoring Ereignis wurde angelegt.');
        $location.path(EVENT_RULE_TABLE);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  /**
   * The user selects an predefined criterium from the sources JSON.
   * The supplied index defined the position in the rule.criteria array.
   * Fill the criterium's view temp value with the criteria from the sources JSON.
   * @param {number} criterionIndex
   */
  $scope.criterionChange = (criterionIndex) => {
    const selectedCriterionId = $scope.rule.criteria[criterionIndex].frontendId;
    $scope.rule.criteria[criterionIndex].view = $scope.sources[0].criteria
      .find((c) => c.frontendId === selectedCriterionId);
    $scope.rule.criteria[criterionIndex].value = '';
  };

  /**
   * Delete the current `EventRule`.
   */
  $scope.deleteRule = () => {
    $scope.hideDeleteDialog();
    monitoringApiService.apiDeleteEventRule($scope.rule.id)
      .then(() => {
        toastService.toast('success', 'Die Ereignis Regel wurde gelöscht.');
        $location.path(EVENT_RULE_TABLE);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  /**
   * Edit the existing `EventRule` and send it to the API server.
   */
  const editRule = () => {
    const postData = JSON.parse(JSON.stringify($scope.rule));
    prepareRuleForBackend(postData);

    monitoringApiService.apiUpdateEventRule(postData)
      .then(() => {
        toastService.toast('success', 'Die Änderungen wurden gespeichert.');
        $location.path(EVENT_RULE_TABLE);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  /**
   * To make an existing rule editable again, add the "view" placeholder values
   * to each of the criteria by searching for them in the sources json.
   * @param {EventRule} rule
   */
  const prepareRuleForFrontend = (rule) => {
    for (const criterium of rule.criteria) {
      criterium.view = $scope.sources[0].criteria
        .find((c) => c.frontendId === criterium.frontendId);
    }

    rule.notifications.task.assignedRoles = rule.notifications.task.assignedRoles.join(',');
    rule.notifications.task.assignedUsers = rule.notifications.task.assignedUsers.join(',');
    rule.notifications.event.assignedUsers = rule.notifications.event.assignedUsers.join(',');

    $scope.rule = rule;
  };

  /**
   * Require the sources and criteria the user can select from the JSON file.
   * Sort criteria and include dumm values for the use as dividers.
   * @returns {object}
   */
  const prepareSources = () => {
    const json = JSON.parse(JSON.stringify($scope.sources));
    const { criteria } = json[0];
    let sortedCriteria = [];

    // Sort by name and category
    sortedCriteria = criteria.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    }).sort((a, b) => {
      if (a.category < b.category) return -1;
      if (a.category > b.category) return 1;
      return 0;
    });

    const categorizedCriteria = [];

    // Add disabled options as named dividers
    for (let i = 0; i < sortedCriteria.length; i += 1) {
      if ((i === 0) || (i > 0 && sortedCriteria[i - 1].category !== sortedCriteria[i].category)) {
        categorizedCriteria.push({
          disabled: true,
          name: sortedCriteria[i].category,
          frontendId: 'placeholder', // to prevent the last placeholder being selected by default
        });
      }
      categorizedCriteria.push(sortedCriteria[i]);
    }

    /**
     * Some keys in sources JSON fromt type 'select' require the current options
     * retreived from the configService
     */
    for (const criterion of categorizedCriteria) {
      if (criterion.type === 'select' && criterion.optionsFromConfig !== undefined) {
        criterion.options = configService.get(criterion.optionsFromConfig);
      }
    }

    json[0].criteria = categorizedCriteria;
    return json;
  };

  /**
   * Add an element to the provided array.
   *
   * @param {Array} array
   */
  $scope.addElement = (array) => {
    array.push({});
  };

  $scope.removeElement = (array, criterionIndex) => {
    array.splice(criterionIndex, 1);
  };

  $scope.showDeleteDialog = () => {
    $scope.twoStepDelete = true;
    interfaceService.applyScope($scope);
  };

  $scope.hideDeleteDialog = () => {
    $scope.twoStepDelete = false;
  };

  $scope.toggleActions = () => {
    $scope.rule.task_actions = $scope.rule.task_actions === null ? [] : null;
  };

  /**
   * Form submit
   * Depending on form mode add a new rule or edit an existing one.
   */
  $scope.addEditRule = () => {
    if ($scope.rule.task_actions) {
      let atleastOneRecepient = false;

      if ($scope.rule.notifications.task.assignedUsers) atleastOneRecepient = true;
      if ($scope.rule.notifications.task.assignedRoles) atleastOneRecepient = true;
      if ($scope.rule.notifications.task.assignedViaAttribute) atleastOneRecepient = true;

      if (!atleastOneRecepient) {
        toastService.toast('error', 'Du musst mindestens einen Empfänger für deine Aufgabe definieren.');
        return null;
      }
    }

    if ($scope.newRule) return addNewRule();
    return editRule();
  };

  /**
   * Form is getting initialized to edit an existing rule
   */
  const initEditRule = () => {
    $scope.preloader = true;
    monitoringApiService.apiGetEventRule($routeParams.ruleId)
      .then((rule) => {
        if (rule.task_actions !== null) $scope.showActions = true;
        prepareRuleForFrontend(rule);
        $scope.selectSource();
        $scope.newRule = false;
        $scope.preloader = false;
        interfaceService.applyScope($scope);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  /**
   * Form is getting initiliazed for the creation of a new rule
   */
  const initNewRule = () => {
    $scope.rule = {
      active: true,
      rule_name: '',
      rule_description: '',
      task_actions: null,
      criteria: [{}],
      run_once: false,
      source: 'cars',
      execute_after: null,
      notifications: {
        task: {
          assignedRoles: null,
          assignedUsers: null,
          autoboard: false,
          email: false,
        },
        event: {
          assignedUsers: null,
          autoboard: false,
          email: false,
        },
      },
    };
    $scope.newRule = true;
    $scope.selectSource();
  };

  $scope.useTemplate = (rule) => {
    $scope.templateToApply = rule;
    $scope.showTemplateDialog();
  };

  $scope.applyTemplate = () => {
    $scope.rule = $scope.templateToApply;
    $scope.templateToApply = null;
    $scope.hideTemplateDialog();
  };

  /** User needs to confirm his choice to use a template */
  $scope.showTemplateDialog = () => {
    $scope.templateDialog = true;
    interfaceService.applyScope($scope);
  };

  $scope.hideTemplateDialog = () => {
    $scope.templateDialog = false;
  };

  const init = () => {
    $scope.sources = prepareSources();
    if ($routeParams.ruleId !== undefined) return initEditRule();
    initNewRule();
    return null;
  };

  init();
}
